"use client";

import type { CSSProperties, MouseEventHandler } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  /** DOM-specific. Defaults to "button". Pass "submit" to use inside a
   *  `<form onSubmit>` for Enter-key support. */
  type?: "button" | "submit" | "reset";
  className?: string;
  style?: CSSProperties;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-surface-brand-strong text-text-on-brand hover:opacity-90 " +
    "focus-visible:ring-2 focus-visible:ring-intent-brand focus-visible:ring-offset-2",
  secondary:
    "border border-border-interactive bg-transparent text-intent-brand " +
    "hover:bg-surface-brand-subtle focus-visible:ring-2 " +
    "focus-visible:ring-intent-brand focus-visible:ring-offset-2",
  ghost:
    "bg-transparent text-text-secondary hover:bg-surface-pressed " +
    "focus-visible:ring-2 focus-visible:ring-text-secondary focus-visible:ring-offset-2",
  destructive:
    "bg-error text-text-on-brand hover:opacity-90 " +
    "focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-2",
};

export default function Button({
  title,
  onPress,
  variant = "primary",
  disabled = false,
  loading = false,
  type = "button",
  className = "",
  style,
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
    onPress();
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-label={title}
      className={
        "inline-flex min-h-11 items-center justify-center rounded-lg px-6 py-3 " +
        "text-button font-bold outline-none transition-opacity " +
        variantClasses[variant] +
        " " +
        (isDisabled ? "opacity-50 cursor-not-allowed " : "") +
        className
      }
      style={style}
    >
      {loading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      ) : (
        title
      )}
    </button>
  );
}
